import PropTypes from 'prop-types';
import React, { memo } from 'react';
import classNames from 'classnames';
import { List, Map } from 'immutable';

import Tabs from '../helpers/Tabs';
import UrlMaker from '../../routing/UrlMaker';
import LoadAnimation from '../decorators/LoadAnimation';
import ContactRouting from '../../routing/ContactRouting';
import { AppHeader, AppHeaderTitle } from '../AppHeader';
import { AppToolbar } from '../AppToolbar';
import { getIn } from '../../utils/ChangeSpy';

import { ContactActions } from './ContactActions';
import { ContactTopPart } from './ContactTopPart';

import style from './style.module.css';

/**
 * Build a URL from a `:contactId`-templated string, preserving the current `contact_slug`
 * (readability only) if one is present in `params`, so tab links and the active-tab highlight
 * stay in sync with whatever's currently in the address bar.
 *
 * `params` may also carry a `contactSlug` key straight from the route match - it's read
 * explicitly here rather than spread into `UrlMaker.mapParams`, since that would otherwise
 * insert a stray `contactSlug=...` query-ish suffix on every URL (including when there's no
 * slug at all, since the key would be present with an `undefined` value).
 *
 * @param {string} to URL template, e.g. `/contact/:contactId/target`.
 * @param {object} params `{ contactId, contactSlug }`.
 * @returns {string} Resolved URL.
 */
function buildUrl(to, { contactId, contactSlug }) {
  const url = UrlMaker.create(to)
    .mapParams({ contactId })
    .replace('/?$', '');

  return contactSlug ? url.replace(`/contact/${contactId}`, `/contact/${contactId}/${contactSlug}`) : url;
}

const ContactComponent = LoadAnimation(
  ({
    showSave,
    onBlurInfo,
    onSubmitInfo,
    onSave,
    tabs,
    values,
    contactInfo,
    contactEventProps,
    children,
    onChange,
    params,
    suffixSuggest,
    ...rest
  }) => {
    const tabsContent = tabs.map(tab => tab.set('to', buildUrl(tab.get('to'), params)));

    const vCardUrl = UrlMaker.create(values.get('vCardUrl')).mapParams({ contactId: params.contactId });

    const availableTabs = Map({
      target: contactInfo.get('hasTargets'),
      buyer: contactInfo.get('hasBuyers'),
      exec: contactInfo.get('isExecutive'),
      lp: contactInfo.get('isLP'),
    });

    if (isNaN(params.contactId)) return children;

    return (
      <div className={classNames(style.host, 'full-height')}>
        <AppHeader>
          <AppHeaderTitle overline="Contact" title={getIn(contactInfo, 'fullName')} />
        </AppHeader>
        <AppToolbar>
          <Tabs availableTabs={availableTabs} className={style.roleTabs} tabs={tabsContent} />
          <ContactActions {...rest} onSave={onSave} showSave={showSave} values={values} vCardUrl={vCardUrl} />
        </AppToolbar>

        <ContactTopPart
          contactEventProps={contactEventProps}
          contactInfo={contactInfo}
          onBlurInfo={onBlurInfo}
          onChange={onChange}
          onSubmitInfo={onSubmitInfo}
          suffixSuggest={suffixSuggest}
        />

        <div className={classNames(style.tabSection, 'container')} role="main">
          <div className={style.tabBody}>
            <ContactRouting />
          </div>
        </div>
      </div>
    );
  },
);

ContactComponent.propTypes = {
  children: PropTypes.node,
  onChange: PropTypes.func.isRequired,
  params: PropTypes.object.isRequired,
  tabs: PropTypes.instanceOf(List).isRequired,
  values: PropTypes.instanceOf(Map).isRequired,
};

/** Contact component. */
export const Contact = memo(ContactComponent);
